import {
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  KeyRound,
  Pencil,
  Plus,
  Radio,
  RefreshCw,
  Square,
  Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../components/AppShell';
import { PreviewPlayer } from '../components/PreviewPlayer';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Empty } from '../components/ui/empty';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../components/ui/toast';
import { ApiError } from '../lib/api/client';
import {
  type CreateLiveSessionResponse,
  type LiveStreamItem,
  type LiveStreamStatus,
  type StreamKey,
  type StreamKeyDestination,
  useCreateStreamKey,
  useCreateStreamKeyDestination,
  useDeleteStreamKeyDestination,
  useEndLiveSession,
  useLiveStreams,
  useRevokeStreamKey,
  useRotateStreamKey,
  useStartObsSession,
  useStreamKeys,
  useUpdateStreamKey,
  useUpdateStreamKeyDestination,
} from '../lib/api/live';
import { fmtRelative } from '../lib/date';
import { useLocaleStore } from '../lib/locales';

/**
 * Unified Broadcasts page — replaces the standalone "OBS broadcast"
 * and "Stream keys" pages with a single surface.
 *
 * Mental model: everything is a stream key. Most are *persistent*
 * (operator-named, reusable for every broadcast, optionally with
 * YouTube/Facebook simulcast destinations). One slot is reserved at
 * the top of the list for an *ad-hoc* session — an ephemeral one-shot
 * key minted via `POST /live/sessions`, mirroring the previous OBS
 * broadcast flow.
 *
 * The detail panel adapts to selection:
 *   - **Persistent key + currently in use** → OBS settings + destinations + live preview.
 *   - **Persistent key + idle**            → OBS settings + destinations.
 *   - **Ad-hoc session**                   → OBS settings + live preview + stop button.
 *
 * Active-ad-hoc session id is persisted in localStorage so a refresh
 * doesn't orphan an in-flight broadcast (the API rejects a second
 * concurrent `POST /live/sessions` from the same user with a 409).
 */

// ── Storage / shared helpers ──────────────────────────────────────────────

const ADHOC_STORAGE_KEY = 'broadcasts:active-adhoc-session';
const OBS_PANEL_KEY = 'broadcasts:obs-panel-open';
const LOCALE_NONE = '__none__';

interface PersistedAdHoc {
  id: string;
  streamKey: string;
  pushUrlRtmp: string;
  title: string;
}

function readPersistedAdHoc(): PersistedAdHoc | null {
  try {
    const raw = localStorage.getItem(ADHOC_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.id === 'string' &&
      typeof parsed?.streamKey === 'string' &&
      typeof parsed?.pushUrlRtmp === 'string'
    ) {
      return { ...parsed, title: typeof parsed.title === 'string' ? parsed.title : '' };
    }
  } catch {
    /* fall through */
  }
  return null;
}

function writePersistedAdHoc(value: PersistedAdHoc | null): void {
  try {
    if (value) localStorage.setItem(ADHOC_STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(ADHOC_STORAGE_KEY);
  } catch {
    /* private mode etc. — ignore */
  }
}

function readPanelOpen(): boolean {
  try {
    const raw = localStorage.getItem(OBS_PANEL_KEY);
    if (raw === '0') return false;
  } catch {
    /* fall through */
  }
  return true;
}

function writePanelOpen(open: boolean): void {
  try {
    localStorage.setItem(OBS_PANEL_KEY, open ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function splitRtmpUrl(pushUrl: string): { server: string; key: string } {
  const lastSlash = pushUrl.lastIndexOf('/');
  if (lastSlash < 0 || lastSlash === pushUrl.length - 1) {
    return { server: pushUrl, key: '' };
  }
  return {
    server: pushUrl.slice(0, lastSlash),
    key: pushUrl.slice(lastSlash + 1),
  };
}

type Selection = { type: 'adhoc' } | { type: 'persistent'; id: string } | null;

// ── Page ──────────────────────────────────────────────────────────────────

const PLATFORMS = [
  { value: 'youtube', label: 'YouTube Live' },
  { value: 'facebook', label: 'Facebook Live' },
  { value: 'twitch', label: 'Twitch' },
  { value: 'custom', label: 'Custom RTMP' },
];

const PLATFORM_HINTS: Record<string, string> = {
  youtube: 'rtmp://a.rtmp.youtube.com/live2',
  facebook: 'rtmps://live-api-s.facebook.com:443/rtmp',
  twitch: 'rtmp://live.twitch.tv/app',
  custom: '',
};

export default function BroadcastsPage(): JSX.Element {
  const toast = useToast();
  const keys = useStreamKeys();
  const live = useLiveStreams();
  const start = useStartObsSession();
  const end = useEndLiveSession();

  const [adhoc, setAdhoc] = useState<PersistedAdHoc | null>(() => readPersistedAdHoc());
  const [selection, setSelection] = useState<Selection>(null);
  const [showStartAdhoc, setShowStartAdhoc] = useState(false);
  const [showCreateKey, setShowCreateKey] = useState(false);

  useEffect(() => {
    writePersistedAdHoc(adhoc);
  }, [adhoc]);

  const items = keys.data?.items ?? [];
  const streams = live.data?.streams ?? [];

  // Default selection on first render: the active ad-hoc if any,
  // else the first persistent key.
  if (selection === null && (adhoc || items.length > 0)) {
    setSelection(adhoc ? { type: 'adhoc' } : { type: 'persistent', id: items[0].id });
  }

  // Find live telemetry for a persistent key (matches by source id) or
  // an ad-hoc session (matches by stream id). Returns null when no
  // active stream matches — the detail panel renders "idle" state.
  const liveForPersistent = (keyId: string): LiveStreamItem | null =>
    streams.find((s) => s.sourceStreamKeyId === keyId) ?? null;
  const liveForAdHoc = (sessionId: string): LiveStreamItem | null =>
    streams.find((s) => s.id === sessionId) ?? null;

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.showToast({ title: `${label} copied`, variant: 'success' });
    } catch {
      toast.showToast({ title: 'Clipboard not available', variant: 'danger' });
    }
  };

  const onStartAdhoc = async (title: string) => {
    try {
      const result: CreateLiveSessionResponse = await start.mutateAsync({
        title: title.trim() || undefined,
      });
      const next = {
        id: result.id,
        streamKey: result.streamKey,
        pushUrlRtmp: result.pushUrlRtmp,
        title: title.trim() || '(untitled)',
      };
      setAdhoc(next);
      setSelection({ type: 'adhoc' });
      setShowStartAdhoc(false);
      toast.showToast({ title: 'Ad-hoc broadcast ready', variant: 'success' });
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 409
          ? 'An active session is already running. Stop it before starting another.'
          : err instanceof Error
            ? err.message
            : 'Could not start broadcast';
      toast.showToast({ title: message, variant: 'danger' });
    }
  };

  const onStopAdhoc = async () => {
    if (!adhoc) return;
    try {
      await end.mutateAsync({ id: adhoc.id });
      toast.showToast({ title: 'Broadcast ended', variant: 'success' });
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 404)) {
        toast.showToast({
          title: err instanceof Error ? err.message : 'Could not stop broadcast',
          variant: 'danger',
        });
      }
    } finally {
      setAdhoc(null);
      // Snap selection back to the first persistent key (or null if
      // there isn't one) so the now-empty ad-hoc row doesn't stay
      // highlighted.
      setSelection(items.length > 0 ? { type: 'persistent', id: items[0].id } : null);
    }
  };

  const selectedKey = useMemo(
    () =>
      selection?.type === 'persistent' ? items.find((k) => k.id === selection.id) ?? null : null,
    [items, selection],
  );

  return (
    <>
      <Topbar title="Broadcasts" />
      <PageHeader
        title="Broadcasts"
        subtitle="Manage persistent OBS keys and simulcast destinations, or start an ad-hoc broadcast. Streams land in the Live contributions green room when they go on air."
        actions={
          <Button onClick={() => setShowCreateKey(true)}>
            <Plus className="mr-1.5 size-4" /> New stream key
          </Button>
        }
      />
      <div className="grid grid-cols-1 gap-6 px-4 pb-10 sm:px-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        {/* ── Left rail ──────────────────────────────────────────── */}
        <div className="space-y-3">
          <Button
            variant={adhoc ? 'outline' : 'primary'}
            className="w-full"
            onClick={() => setShowStartAdhoc(true)}
            disabled={!!adhoc}
          >
            <Radio className="mr-1.5 size-4" />
            {adhoc ? 'Ad-hoc broadcast running' : 'Quick ad-hoc broadcast'}
          </Button>

          {adhoc ? (
            <button
              type="button"
              onClick={() => setSelection({ type: 'adhoc' })}
              className={`flex w-full flex-col rounded-md border p-3 text-left transition-colors ${
                selection?.type === 'adhoc'
                  ? 'border-brand bg-brand-soft/40'
                  : 'border-line hover:bg-surface-muted'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1.5 truncate font-medium">
                  <Clock className="size-3.5 shrink-0 text-muted-foreground" />
                  {adhoc.title || '(untitled)'}
                </span>
                <Badge variant="warning">Ad-hoc</Badge>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">One-shot session</div>
            </button>
          ) : null}

          {keys.isLoading && !keys.data ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner className="size-4" /> Loading…
            </div>
          ) : null}

          {!adhoc && items.length === 0 && !keys.isLoading ? (
            <Empty
              title="No stream keys yet"
              description="Create a persistent key for OBS, or start a quick ad-hoc broadcast above."
            />
          ) : null}

          {items.map((k) => {
            const isLive = !!liveForPersistent(k.id);
            return (
              <button
                key={k.id}
                type="button"
                onClick={() => setSelection({ type: 'persistent', id: k.id })}
                className={`flex w-full flex-col rounded-md border p-3 text-left transition-colors ${
                  selection?.type === 'persistent' && selection.id === k.id
                    ? 'border-brand bg-brand-soft/40'
                    : 'border-line hover:bg-surface-muted'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">{k.name}</span>
                  {isLive ? (
                    <Badge variant="live">Live</Badge>
                  ) : !k.enabled || k.revokedAt ? (
                    <Badge variant="danger">Disabled</Badge>
                  ) : (
                    <Badge variant="success">Active</Badge>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                  {k.localeCode ? <span>{k.localeCode}</span> : null}
                  {k.localeCode ? <span>·</span> : null}
                  <span>
                    {k.destinations.length} destination{k.destinations.length === 1 ? '' : 's'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Detail panel ──────────────────────────────────────────── */}
        <div className="space-y-6">
          {selection?.type === 'adhoc' && adhoc ? (
            <AdHocDetail
              adhoc={adhoc}
              liveItem={liveForAdHoc(adhoc.id)}
              stopping={end.isPending}
              onStop={onStopAdhoc}
              copy={copy}
            />
          ) : null}

          {selection?.type === 'persistent' && selectedKey ? (
            <PersistentDetail
              streamKey={selectedKey}
              liveItem={liveForPersistent(selectedKey.id)}
              ingestHost={keys.data?.ingestHost ?? null}
              copy={copy}
            />
          ) : null}

          {!selection ? (
            <Card>
              <CardContent className="p-8 text-sm text-muted-foreground">
                Select a stream key from the list to see its push URLs, destinations, and any live
                preview.
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      {showStartAdhoc ? (
        <StartAdHocDialog
          onSubmit={onStartAdhoc}
          starting={start.isPending}
          onClose={() => setShowStartAdhoc(false)}
        />
      ) : null}
      {showCreateKey ? <CreateKeyDialog onClose={() => setShowCreateKey(false)} /> : null}
    </>
  );
}

// ── Ad-hoc detail panel ──────────────────────────────────────────────────

function AdHocDetail({
  adhoc,
  liveItem,
  stopping,
  onStop,
  copy,
}: {
  adhoc: PersistedAdHoc;
  liveItem: LiveStreamItem | null;
  stopping: boolean;
  onStop: () => void;
  copy: (text: string, label: string) => void;
}): JSX.Element {
  const { server, key } = useMemo(() => splitRtmpUrl(adhoc.pushUrlRtmp), [adhoc.pushUrlRtmp]);
  const status: LiveStreamStatus = liveItem?.status ?? 'pending';
  const [obsOpen, setObsOpen] = useState<boolean>(() => readPanelOpen());

  useEffect(() => {
    writePanelOpen(obsOpen);
  }, [obsOpen]);

  return (
    <div
      className={`grid gap-6 ${obsOpen ? 'lg:grid-cols-[minmax(0,1fr)_340px]' : 'lg:grid-cols-1'}`}
    >
      <Card>
        <CardHeader className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CardTitle>Live status</CardTitle>
              <StatusBadge status={status} />
            </div>
            <CardDescription>
              Ad-hoc broadcast{adhoc.title && adhoc.title !== '(untitled)' ? ` — "${adhoc.title}"` : ''}.
              Stops when you click below or close the browser. For recurring broadcasts, create a
              persistent stream key instead.
            </CardDescription>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
            {!obsOpen ? (
              <Button variant="outline" size="sm" onClick={() => setObsOpen(true)}>
                <ChevronDown className="mr-1.5 size-3.5" />
                OBS settings
              </Button>
            ) : null}
            <Button variant="danger" onClick={onStop} disabled={stopping}>
              {stopping ? <Spinner className="mr-2 size-4" /> : <Square className="mr-2 size-4" />}
              Stop broadcast
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Stats item={liveItem} />
          <LivePreview liveItem={liveItem} resetKey={adhoc.streamKey} />
        </CardContent>
      </Card>

      {obsOpen ? (
        <ObsSettingsCard
          server={server}
          streamKey={key}
          onClose={() => setObsOpen(false)}
          copy={copy}
        />
      ) : null}
    </div>
  );
}

// ── Persistent-key detail panel ──────────────────────────────────────────

function PersistentDetail({
  streamKey,
  liveItem,
  ingestHost,
  copy,
}: {
  streamKey: StreamKey;
  liveItem: LiveStreamItem | null;
  ingestHost: string | null;
  copy: (text: string, label: string) => void;
}): JSX.Element {
  const [editingMeta, setEditingMeta] = useState(false);
  const [showRotate, setShowRotate] = useState(false);
  const [showRevoke, setShowRevoke] = useState(false);
  const [showCreateDest, setShowCreateDest] = useState(false);
  const [editingDest, setEditingDest] = useState<StreamKeyDestination | null>(null);
  const update = useUpdateStreamKey(streamKey.id);
  const toast = useToast();
  const isLive = !!liveItem;

  const { server, keyPart } = useMemo(() => {
    const url = streamKey.pushUrlRtmp;
    if (!url) return { server: null, keyPart: streamKey.streamKey };
    const lastSlash = url.lastIndexOf('/');
    if (lastSlash < 0 || lastSlash === url.length - 1) {
      return { server: url, keyPart: '' };
    }
    return { server: url.slice(0, lastSlash), keyPart: url.slice(lastSlash + 1) };
  }, [streamKey.pushUrlRtmp, streamKey.streamKey]);

  return (
    <div className="space-y-6">
      {isLive ? (
        <Card>
          <CardHeader className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <CardTitle>Live status</CardTitle>
                <StatusBadge status={liveItem.status} />
              </div>
              <CardDescription>
                Real-time telemetry + preview from the ingest server. To cut the feed, use the
                Live contributions green room.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Stats item={liveItem} />
            <LivePreview liveItem={liveItem} resetKey={streamKey.streamKey} />
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle>{streamKey.name}</CardTitle>
            <CardDescription>
              {streamKey.localeCode ? (
                <span className="mr-2 text-xs uppercase tracking-wide">{streamKey.localeCode}</span>
              ) : null}
              Created {fmtRelative(streamKey.createdAt)}
              {streamKey.createdBy ? <> · by {streamKey.createdBy.email}</> : null}
              {streamKey.revokedAt ? <> · revoked {fmtRelative(streamKey.revokedAt)}</> : null}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditingMeta(true)}>
              <Pencil className="mr-1.5 size-3.5" /> Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRotate(true)}
              disabled={!!streamKey.revokedAt}
            >
              <RefreshCw className="mr-1.5 size-3.5" /> Rotate key
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowRevoke(true)}
              disabled={!!streamKey.revokedAt}
            >
              <Trash2 className="mr-1.5 size-3.5" /> Revoke
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Enabled</div>
              <div className="text-sm text-muted-foreground">
                Disabling stops new broadcasts from being accepted without revoking the key.
              </div>
            </div>
            <Switch
              checked={streamKey.enabled && !streamKey.revokedAt}
              disabled={!!streamKey.revokedAt}
              onCheckedChange={(checked) => {
                update.mutate(
                  { enabled: checked },
                  {
                    onError: (err) =>
                      toast.showToast({
                        title: err instanceof Error ? err.message : 'Update failed',
                        variant: 'danger',
                      }),
                  },
                );
              }}
            />
          </div>

          <CopyRow
            label="OBS server"
            value={server ?? '(ingest host not configured)'}
            onCopy={() => server && copy(server, 'Server URL')}
            disabled={!server}
          />
          <CopyRow
            label="Stream key"
            mono
            value={keyPart}
            onCopy={() => copy(keyPart, 'Stream key')}
          />
          {streamKey.pushUrlSrt ? (
            <CopyRow
              label="SRT push URL (alt)"
              mono
              value={streamKey.pushUrlSrt}
              onCopy={() => copy(streamKey.pushUrlSrt!, 'SRT push URL')}
            />
          ) : null}
          {streamKey.notes ? (
            <p className="text-sm text-muted-foreground">{streamKey.notes}</p>
          ) : null}
          {!ingestHost ? (
            <p className="text-xs text-amber-600">
              `LIVE_INGEST_HOST` isn't configured on the API — push URLs can't be built. Set it and
              redeploy.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle>Simulcast destinations</CardTitle>
            <CardDescription>
              Each enabled destination receives an ffmpeg-copy of the broadcast. Configure
              YouTube, Facebook, or any other RTMP/RTMPS endpoint — they all push in parallel while
              the stream is live.
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowCreateDest(true)}>
            <Plus className="mr-1.5 size-3.5" /> Add destination
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {streamKey.destinations.length === 0 ? (
            <p className="text-sm text-muted-foreground">No destinations yet.</p>
          ) : (
            streamKey.destinations.map((d) => (
              <DestinationRow
                key={d.id}
                streamKeyId={streamKey.id}
                destination={d}
                onEdit={() => setEditingDest(d)}
              />
            ))
          )}
        </CardContent>
      </Card>

      {editingMeta ? (
        <EditKeyDialog streamKey={streamKey} onClose={() => setEditingMeta(false)} />
      ) : null}
      {showRotate ? (
        <RotateKeyDialog streamKey={streamKey} onClose={() => setShowRotate(false)} />
      ) : null}
      {showRevoke ? (
        <RevokeKeyDialog streamKey={streamKey} onClose={() => setShowRevoke(false)} />
      ) : null}
      {showCreateDest ? (
        <DestinationDialog
          streamKeyId={streamKey.id}
          onClose={() => setShowCreateDest(false)}
        />
      ) : null}
      {editingDest ? (
        <DestinationDialog
          streamKeyId={streamKey.id}
          destination={editingDest}
          onClose={() => setEditingDest(null)}
        />
      ) : null}
    </div>
  );
}

// ── Shared OBS settings / preview / stats ────────────────────────────────

function ObsSettingsCard({
  server,
  streamKey,
  onClose,
  copy,
}: {
  server: string;
  streamKey: string;
  onClose: () => void;
  copy: (text: string, label: string) => void;
}): JSX.Element {
  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">OBS settings</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Hide OBS settings">
            <ChevronUp className="size-4" />
          </Button>
        </div>
        <CardDescription className="text-xs">
          Open OBS → Settings → Stream → Service: <strong>Custom…</strong> Paste server + stream
          key below, then <strong>Start Streaming</strong>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <CopyRow label="Server" value={server} onCopy={() => copy(server, 'Server URL')} />
        <CopyRow
          label="Stream key"
          value={streamKey}
          mono
          onCopy={() => copy(streamKey, 'Stream key')}
        />
        <p className="text-xs text-muted-foreground">
          Recommended: H.264, ~2 500–4 500 kbps, keyframe interval 2 s, 48 kHz AAC stereo. Server
          bitrate ceiling is 60 Mbps shared across all live contributors.
        </p>
      </CardContent>
    </Card>
  );
}

function LivePreview({
  liveItem,
  resetKey,
}: {
  liveItem: LiveStreamItem | null;
  resetKey: string;
}): JSX.Element {
  if (liveItem && (liveItem.hlsPreviewUrl || liveItem.webrtcPlayUrl)) {
    return (
      <PreviewPlayer
        webrtcUrl={liveItem.webrtcPlayUrl}
        hlsUrl={liveItem.hlsPreviewUrl}
        resetKey={resetKey}
      />
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      Waiting for the first frame from the encoder…
    </p>
  );
}

function StatusBadge({ status }: { status: LiveStreamStatus }): JSX.Element {
  switch (status) {
    case 'pushing':
      return <Badge variant="success">Pushing</Badge>;
    case 'on_air':
      return <Badge variant="live">On air</Badge>;
    case 'rejected':
      return <Badge variant="danger">Rejected</Badge>;
    case 'ended':
      return <Badge>Ended</Badge>;
    case 'pending':
    default:
      return <Badge variant="warning">Pending</Badge>;
  }
}

function Stats({ item }: { item: LiveStreamItem | null }): JSX.Element {
  if (!item) {
    return (
      <p className="text-sm text-muted-foreground">No telemetry yet — connect your encoder to start.</p>
    );
  }
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
      <Stat label="Bitrate" value={item.bitrateKbps != null ? `${item.bitrateKbps} kbps` : '—'} />
      <Stat
        label="Packet loss"
        value={item.packetLossPct != null ? `${item.packetLossPct.toFixed(1)}%` : '—'}
      />
      <Stat
        label="Pushing since"
        value={item.pushStartedAt ? fmtRelative(item.pushStartedAt) : '—'}
      />
      <Stat label="Slot" value={item.onAirSlot != null ? `Slot ${item.onAirSlot}` : '—'} />
    </dl>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

function CopyRow({
  label,
  value,
  mono,
  onCopy,
  disabled,
}: {
  label: string;
  value: string;
  mono?: boolean;
  onCopy: () => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-2">
        <code
          className={`min-w-0 flex-1 truncate rounded-md border bg-muted/40 px-3 py-2 text-sm${
            mono ? ' font-mono' : ''
          }`}
        >
          {value || '—'}
        </code>
        <Button variant="outline" size="sm" onClick={onCopy} disabled={disabled}>
          <Copy className="mr-1.5 size-3.5" />
          Copy
        </Button>
      </div>
    </div>
  );
}

// ── Destinations (re-used from the original Stream keys page) ────────────

function DestinationRow({
  streamKeyId,
  destination,
  onEdit,
}: {
  streamKeyId: string;
  destination: StreamKeyDestination;
  onEdit: () => void;
}): JSX.Element {
  const update = useUpdateStreamKeyDestination(streamKeyId);
  const del = useDeleteStreamKeyDestination(streamKeyId);
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);

  const onDelete = async () => {
    try {
      await del.mutateAsync(destination.id);
      toast.showToast({ title: 'Destination deleted', variant: 'success' });
      setConfirming(false);
    } catch (err) {
      toast.showToast({
        title: err instanceof Error ? err.message : 'Delete failed',
        variant: 'danger',
      });
    }
  };

  return (
    <div className="flex items-center gap-3 rounded-md border bg-muted/20 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{destination.label}</span>
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {destination.platform}
          </span>
        </div>
        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          {destination.rtmpUrl} · key <code className="font-mono">{destination.keyMasked}</code>
        </div>
      </div>
      <Switch
        checked={destination.enabled}
        onCheckedChange={(checked) =>
          update.mutate(
            { id: destination.id, enabled: checked },
            {
              onError: (err) =>
                toast.showToast({
                  title: err instanceof Error ? err.message : 'Update failed',
                  variant: 'danger',
                }),
            },
          )
        }
      />
      <Button variant="outline" size="sm" onClick={onEdit}>
        <Pencil className="size-3.5" />
      </Button>
      <Button variant="danger" size="sm" onClick={() => setConfirming(true)}>
        <Trash2 className="size-3.5" />
      </Button>

      <Dialog open={confirming} onOpenChange={(o) => !o && setConfirming(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete destination “{destination.label}”?</DialogTitle>
            <DialogDescription>
              Removes the {destination.platform} simulcast target. Any in-progress broadcast keeps
              running but stops being forwarded to this destination. This can't be undone — you'll
              need to re-add it (with its stream key) to restore it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={del.isPending}>
              Cancel
            </Button>
            <Button variant="danger" onClick={onDelete} disabled={del.isPending}>
              {del.isPending ? <Spinner className="mr-2 size-4" /> : <Trash2 className="mr-2 size-4" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Dialogs ───────────────────────────────────────────────────────────────

function StartAdHocDialog({
  onSubmit,
  starting,
  onClose,
}: {
  onSubmit: (title: string) => void;
  starting: boolean;
  onClose: () => void;
}): JSX.Element {
  const [title, setTitle] = useState('');
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start ad-hoc broadcast</DialogTitle>
          <DialogDescription>
            Mints a one-shot RTMP key for an unplanned broadcast. The key is single-use — stop the
            broadcast when you're done. For recurring streams, create a persistent stream key
            instead.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="adhoc-title">Title (optional)</Label>
          <Input
            id="adhoc-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Eagle Square rally"
            maxLength={200}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(title)} disabled={starting}>
            {starting ? <Spinner className="mr-2 size-4" /> : <Radio className="mr-2 size-4" />}
            Start broadcast
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateKeyDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const [name, setName] = useState('');
  const [localeCode, setLocaleCode] = useState<string>(LOCALE_NONE);
  const [notes, setNotes] = useState('');
  const create = useCreateStreamKey();
  const toast = useToast();

  const submit = async () => {
    try {
      await create.mutateAsync({
        name: name.trim(),
        localeCode: localeCode === LOCALE_NONE ? null : localeCode,
        notes: notes.trim() || null,
      });
      toast.showToast({ title: 'Stream key created', variant: 'success' });
      onClose();
    } catch (err) {
      toast.showToast({
        title: err instanceof Error ? err.message : 'Create failed',
        variant: 'danger',
      });
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New stream key</DialogTitle>
          <DialogDescription>
            Pick a memorable name — operators see this in the green room when the stream goes live.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="new-key-name">Name</Label>
            <Input
              id="new-key-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Hausa Evening News"
              maxLength={120}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-key-locale">Locale tag (optional)</Label>
            <LocaleSelect value={localeCode} onValueChange={setLocaleCode} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-key-notes">Notes (optional)</Label>
            <Textarea
              id="new-key-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              placeholder="Who uses this key and from where?"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || create.isPending}>
            {create.isPending ? <Spinner className="mr-2 size-4" /> : <KeyRound className="mr-2 size-4" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LocaleSelect({
  value,
  onValueChange,
}: {
  value: string;
  onValueChange: (v: string) => void;
}): JSX.Element {
  const available = useLocaleStore((s) => s.available);
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={LOCALE_NONE}>No locale</SelectItem>
        {available.map((l) => (
          <SelectItem key={l.code} value={l.code}>
            {l.label} <span className="text-muted-foreground">({l.code})</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function EditKeyDialog({
  streamKey,
  onClose,
}: {
  streamKey: StreamKey;
  onClose: () => void;
}): JSX.Element {
  const [name, setName] = useState(streamKey.name);
  const [localeCode, setLocaleCode] = useState<string>(streamKey.localeCode ?? LOCALE_NONE);
  const [notes, setNotes] = useState(streamKey.notes ?? '');
  const update = useUpdateStreamKey(streamKey.id);
  const toast = useToast();

  const submit = async () => {
    try {
      await update.mutateAsync({
        name: name.trim(),
        localeCode: localeCode === LOCALE_NONE ? null : localeCode,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (err) {
      toast.showToast({
        title: err instanceof Error ? err.message : 'Update failed',
        variant: 'danger',
      });
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit key</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="edit-key-name">Name</Label>
            <Input
              id="edit-key-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-key-locale">Locale tag</Label>
            <LocaleSelect value={localeCode} onValueChange={setLocaleCode} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-key-notes">Notes</Label>
            <Textarea
              id="edit-key-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || update.isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RotateKeyDialog({
  streamKey,
  onClose,
}: {
  streamKey: StreamKey;
  onClose: () => void;
}): JSX.Element {
  const rotate = useRotateStreamKey(streamKey.id);
  const toast = useToast();
  const submit = async () => {
    try {
      await rotate.mutateAsync();
      toast.showToast({
        title: 'Key rotated — update OBS with the new value',
        variant: 'success',
      });
      onClose();
    } catch (err) {
      toast.showToast({
        title: err instanceof Error ? err.message : 'Rotate failed',
        variant: 'danger',
      });
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rotate stream key?</DialogTitle>
          <DialogDescription>
            A new random key replaces the current one immediately. Any encoder still using the old
            key will be disconnected at its next reconnect.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={rotate.isPending}>
            {rotate.isPending ? <Spinner className="mr-2 size-4" /> : null}
            Rotate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevokeKeyDialog({
  streamKey,
  onClose,
}: {
  streamKey: StreamKey;
  onClose: () => void;
}): JSX.Element {
  const revoke = useRevokeStreamKey();
  const toast = useToast();
  const submit = async () => {
    try {
      await revoke.mutateAsync(streamKey.id);
      toast.showToast({ title: 'Stream key revoked', variant: 'success' });
      onClose();
    } catch (err) {
      toast.showToast({
        title: err instanceof Error ? err.message : 'Revoke failed',
        variant: 'danger',
      });
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke "{streamKey.name}"?</DialogTitle>
          <DialogDescription>
            The key stops accepting new broadcasts immediately. Existing live broadcasts continue
            until they end. Revocation can't be undone — create a fresh key if you need to bring
            this stream back later.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={submit} disabled={revoke.isPending}>
            {revoke.isPending ? <Spinner className="mr-2 size-4" /> : null}
            Revoke
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DestinationDialog({
  streamKeyId,
  destination,
  onClose,
}: {
  streamKeyId: string;
  destination?: StreamKeyDestination;
  onClose: () => void;
}): JSX.Element {
  const [platform, setPlatform] = useState(destination?.platform ?? 'youtube');
  const [label, setLabel] = useState(destination?.label ?? '');
  const [rtmpUrl, setRtmpUrl] = useState(destination?.rtmpUrl ?? PLATFORM_HINTS.youtube ?? '');
  const [destinationKey, setDestinationKey] = useState('');
  const [enabled, setEnabled] = useState(destination?.enabled ?? true);
  const create = useCreateStreamKeyDestination(streamKeyId);
  const update = useUpdateStreamKeyDestination(streamKeyId);
  const toast = useToast();
  const editing = !!destination;

  const submit = async () => {
    try {
      if (editing) {
        await update.mutateAsync({
          id: destination.id,
          platform,
          label: label.trim(),
          rtmpUrl: rtmpUrl.trim(),
          destinationKey: destinationKey.trim() || undefined,
          enabled,
        });
      } else {
        await create.mutateAsync({
          platform,
          label: label.trim(),
          rtmpUrl: rtmpUrl.trim(),
          destinationKey: destinationKey.trim(),
          enabled,
        });
      }
      toast.showToast({
        title: editing ? 'Destination updated' : 'Destination added',
        variant: 'success',
      });
      onClose();
    } catch (err) {
      toast.showToast({
        title: err instanceof Error ? err.message : 'Save failed',
        variant: 'danger',
      });
    }
  };

  const onPlatformChange = (value: string) => {
    setPlatform(value);
    if (!editing) {
      const hint = PLATFORM_HINTS[value];
      if (hint !== undefined) setRtmpUrl(hint);
    }
  };

  const canSubmit = label.trim() && rtmpUrl.trim() && (editing || destinationKey.trim());

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit destination' : 'Add destination'}</DialogTitle>
          <DialogDescription>
            The destination key is encrypted at rest. Edit later only if it changes — leave it
            blank when editing to keep the existing value.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Platform</Label>
            <Select value={platform} onValueChange={onPlatformChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLATFORMS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dest-label">Label</Label>
            <Input
              id="dest-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="VON Hausa YouTube channel"
              maxLength={120}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dest-url">Server URL</Label>
            <Input
              id="dest-url"
              value={rtmpUrl}
              onChange={(e) => setRtmpUrl(e.target.value)}
              placeholder="rtmp://a.rtmp.youtube.com/live2"
              maxLength={500}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dest-key">
              Stream key{' '}
              {editing ? <span className="text-muted-foreground">(leave blank to keep)</span> : null}
            </Label>
            <Input
              id="dest-key"
              type="password"
              autoComplete="off"
              value={destinationKey}
              onChange={(e) => setDestinationKey(e.target.value)}
              placeholder={editing ? '••••••••' : 'paste the destination platform stream key'}
            />
          </div>
          <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
            <Label className="text-sm" htmlFor="dest-enabled">
              Enabled
            </Label>
            <Switch id="dest-enabled" checked={enabled} onCheckedChange={setEnabled} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit || create.isPending || update.isPending}>
            {(create.isPending || update.isPending) ? <Spinner className="mr-2 size-4" /> : null}
            {editing ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
